import { indentWithTab } from "@codemirror/commands";
import { css } from "@codemirror/lang-css";
import { html } from "@codemirror/lang-html";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { EditorState, Extension } from "@codemirror/state";
import { EditorView, keymap } from "@codemirror/view";
import { useHandler } from "@redotech/react-util/hook";
import { basicSetup } from "codemirror";
import {
  ForwardedRef,
  forwardRef,
  memo,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDebounce } from "usehooks-ts";
import { LabelText } from "./arbiter-components/common/descriptors";
import * as styles from "./code-editor.module.css";
import { Flex } from "./flex";

interface CodeEditorProps {
  state: EditorState;
  className?: string;
}

export type CodeEditorLanguage =
  | "markdown"
  | "javascript"
  | "html"
  | "css"
  | "json";

// Styles CodeMirror lint diagnostics as a Redo popover so every editor in the
// app gets consistent tooltips. z-index clears floating panels (e.g. z 1000).
const redoDiagnosticTheme = EditorView.theme({
  ".cm-tooltip": {
    zIndex: "1001",
    border: "1px solid var(--redo-colors-border-border-secondary)",
    borderRadius: "var(--border-radius-lg)",
    background: "var(--redo-colors-background-bg-primary)",
    boxShadow:
      "0px 4px 8px -2px var(--redo-shadow-color-primary), 0px 2px 4px -2px var(--redo-shadow-color-secondary)",
    fontFamily: "var(--redo-body-font-family)",
    color: "var(--redo-colors-text-text-primary)",
  },
  ".cm-tooltip-lint": { maxWidth: "320px" },
  ".cm-diagnostic": {
    display: "flex",
    flexDirection: "column",
    gap: "var(--redo-spacing-md)",
    margin: "0",
    padding: "var(--redo-spacing-md)",
    borderLeft: "none",
    fontSize: "13px",
    lineHeight: "1.45",
    whiteSpace: "normal",
    color: "var(--redo-colors-text-text-secondary)",
  },
  ".cm-diagnostic code": {
    fontFamily: "var(--redo-font-family-mono)",
    fontSize: "12px",
    padding: "1px 4px",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--redo-colors-background-bg-secondary)",
    color: "var(--redo-colors-text-text-primary)",
  },
  ".cm-diagnosticAction": {
    display: "block",
    width: "100%",
    margin: "0",
    padding: "6px 12px",
    borderRadius: "var(--border-radius-lg)",
    border: "1px solid var(--redo-colors-border-border-secondary)",
    background: "var(--redo-colors-background-bg-primary)",
    color: "var(--redo-colors-text-text-primary)",
    fontWeight: "600",
    fontSize: "13px",
    textAlign: "center",
    cursor: "pointer",
  },
  ".cm-diagnosticAction:hover": {
    background: "var(--redo-colors-background-bg-secondary)",
  },
});

function createEditorState({
  initialValue,
  language,
  readonly,
  onUpdate,
  extensions = [],
}: {
  initialValue: string;
  language: CodeEditorLanguage;
  readonly?: boolean;
  onUpdate: (value: string) => void;
  extensions?: Extension[];
}): EditorState {
  const getLanguageExtension = () => {
    switch (language) {
      case "markdown":
        return markdown();
      case "javascript":
        return javascript();
      case "html":
        return html();
      case "css":
        return css();
      case "json":
        return json();
      default:
        return [];
    }
  };

  // Default extensions
  const defaultExtensions = [
    basicSetup,
    keymap.of([indentWithTab]),
    getLanguageExtension(),
    redoDiagnosticTheme,
    EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        onUpdate?.(update.state.doc.toString());
      }
    }),
  ];
  if (readonly) {
    defaultExtensions.push(EditorView.editable.of(false));
  }
  return EditorState.create({
    doc: initialValue,
    extensions: [...defaultExtensions, ...extensions],
  });
}

const CodeEditor = memo(
  forwardRef(function CodeEditor(
    { state, className = "" }: CodeEditorProps,
    ref: ForwardedRef<EditorView>,
  ) {
    const editorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!editorRef.current) {
        return;
      }

      const view = new EditorView({ state, parent: editorRef.current });

      if (typeof ref === "function") {
        ref(view);
      } else if (ref) {
        ref.current = view;
      }

      return () => {
        view.destroy();
        if (typeof ref === "function") {
          ref(null);
        } else if (ref) {
          ref.current = null;
        }
      };
    }, [state, ref]);

    return (
      <div className={`${styles.container} ${className}`}>
        <div className={styles.editor} ref={editorRef} />
      </div>
    );
  }),
);

export interface CodeEditorInputProps extends Omit<CodeEditorProps, "state"> {
  label?: string;
  language: CodeEditorLanguage;
  value: string;
  setValue(value: string): void;
  readonly?: boolean;
  extensions?: Extension[];
  onEditorView?: (view: EditorView | null) => void;
  /** Render at a fixed default height with a native drag handle so the
   *  user can extend the editor downward. */
  resizable?: boolean;
}

export const CodeEditorInputField = memo(function CodeEditorInputField({
  label,
  language,
  value,
  setValue,
  readonly,
  extensions,
  onEditorView,
  resizable,
  className = "",
  ...rest
}: CodeEditorInputProps) {
  const ref = useRef<EditorView | null>(null);
  const onUpdate = useHandler((value: string) => {
    setValue(value);
  });

  const [state] = useState<EditorState>(() =>
    createEditorState({
      initialValue: value,
      language,
      readonly,
      onUpdate,
      extensions,
    }),
  );

  const debouncedValue = useDebounce(value, 300);

  useEffect(() => {
    const state = ref.current?.state;
    if (state && state.doc.toString() !== debouncedValue) {
      ref.current?.dispatch({
        changes: { from: 0, to: state.doc.length, insert: debouncedValue },
      });
    }
  }, [state, debouncedValue]);

  const onViewRef = useHandler((view: EditorView | null) => {
    ref.current = view;
    onEditorView?.(view);
  });

  return (
    <Flex className={styles.wrapper} dir="column" flex={1} gap="xs">
      {label ? <LabelText label={label} size="xs" /> : null}
      <CodeEditor
        {...rest}
        className={`${resizable ? styles.resizable : ""} ${className}`}
        ref={onViewRef}
        state={state}
      />
    </Flex>
  );
});
